import React, { useState, useEffect } from 'react';
import { FiX, FiTerminal, FiCode, FiCpu, FiDownload } from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';
import { artifactoryService } from '../../services/artifactoryService';
import { HandoffPlugin } from '../../types/artifactory';

interface HandoffDialogProps {
  isOpen: boolean;
  onClose: () => void;
  manifestId: string;
}

const ICON_MAP: Record<string, React.ReactNode> = {
  terminal: <FiTerminal className="w-6 h-6" />,
  code: <FiCode className="w-6 h-6" />,
  cpu: <FiCpu className="w-6 h-6" />,
};

export const HandoffDialog: React.FC<HandoffDialogProps> = ({
  isOpen,
  onClose,
  manifestId,
}) => {
  const { darkMode } = useTheme();

  const [plugins, setPlugins] = useState<HandoffPlugin[]>([]);
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [hydrationStatus, setHydrationStatus] = useState<any>(null);
  const [includeStale, setIncludeStale] = useState(true);
  const [includeGhost, setIncludeGhost] = useState(true);
  const [includeBuildOrder, setIncludeBuildOrder] = useState(true);
  const [includeIntentHistory, setIncludeIntentHistory] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    // Fetch plugins and hydration status in parallel
    Promise.all([
      artifactoryService.getHandoffPlugins(),
      artifactoryService.getProjectHydrationStatus(manifestId),
    ]).then(([pluginData, statusData]) => {
      setPlugins(pluginData);
      setHydrationStatus(statusData);
    }).catch((err) => {
      setError(err.message || 'Failed to load export options');
    });
  }, [isOpen, manifestId]);

  const handleHandoff = () => {
    if (!selectedTool) return;

    setIsExporting(true);
    setError(null);

    try {
      const url = artifactoryService.handoffProject(manifestId, selectedTool, {
        includeStale,
        includeGhost,
        includeBuildOrder,
        includeIntentHistory,
      });

      const link = document.createElement('a');
      link.href = url;
      link.download = `project_handoff_${selectedTool}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  const totalNodes = hydrationStatus
    ? (hydrationStatus.fresh_nodes || 0) + (hydrationStatus.stale_nodes || 0) + (hydrationStatus.ghost_nodes || 0) + (hydrationStatus.locked_nodes || 0) + (hydrationStatus.pending_nodes || 0) + (hydrationStatus.hydrating_nodes || 0)
    : 0;
  const hydrationPct = totalNodes > 0
    ? Math.round(((hydrationStatus?.fresh_nodes || 0) + (hydrationStatus?.locked_nodes || 0)) / totalNodes * 100)
    : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`w-[520px] rounded-lg shadow-xl ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <h2 className="text-lg font-semibold">Handoff to External Agent</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-600 rounded">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Tool Picker */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Target Tool
            </label>
            <div className="grid grid-cols-3 gap-3">
              {plugins.map((plugin) => (
                <button
                  key={plugin.name}
                  onClick={() => setSelectedTool(plugin.name)}
                  className={`p-3 rounded-lg border-2 text-center transition-colors ${
                    selectedTool === plugin.name
                      ? 'border-blue-500 bg-blue-500 bg-opacity-10'
                      : darkMode
                        ? 'border-gray-600 hover:border-gray-500'
                        : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex justify-center mb-2">
                    {ICON_MAP[plugin.icon] || <FiCpu className="w-6 h-6" />}
                  </div>
                  <div className="text-sm font-medium">{plugin.display_name}</div>
                  <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {plugin.instruction_filename}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Options */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Options
            </label>
            <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-750' : 'bg-gray-50'} space-y-2`}>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm">Include stale files (with warnings)</span>
                <div className="flex items-center">
                  {hydrationStatus && (
                    <span className={`mr-2 px-2 py-0.5 text-xs rounded-full ${
                      darkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-800'
                    }`}>{hydrationStatus.stale_nodes || 0}</span>
                  )}
                  <input type="checkbox" checked={includeStale} onChange={(e) => setIncludeStale(e.target.checked)} />
                </div>
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm">Include ghost stubs (with TODOs)</span>
                <div className="flex items-center">
                  {hydrationStatus && (
                    <span className={`mr-2 px-2 py-0.5 text-xs rounded-full ${
                      darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-800'
                    }`}>{hydrationStatus.ghost_nodes || 0}</span>
                  )}
                  <input type="checkbox" checked={includeGhost} onChange={(e) => setIncludeGhost(e.target.checked)} />
                </div>
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm">Include build order</span>
                <input type="checkbox" checked={includeBuildOrder} onChange={(e) => setIncludeBuildOrder(e.target.checked)} />
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm">Include intent history</span>
                <input type="checkbox" checked={includeIntentHistory} onChange={(e) => setIncludeIntentHistory(e.target.checked)} />
              </label>
            </div>
          </div>

          {/* Status Bar */}
          {hydrationStatus && (
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Project Status
              </label>
              <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                <div className="flex justify-between text-xs mb-2">
                  <span className="text-green-500">{'\u25CF'} {hydrationStatus.fresh_nodes || 0} Fresh</span>
                  <span className="text-orange-500">{'\u25CF'} {hydrationStatus.stale_nodes || 0} Stale</span>
                  <span className="text-gray-500">{'\u25CF'} {hydrationStatus.ghost_nodes || 0} Ghost</span>
                </div>
                <div className="w-full h-2 bg-gray-600 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full transition-all"
                    style={{ width: `${hydrationPct}%` }}
                  />
                </div>
                <div className={`text-xs mt-1 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {hydrationPct}% hydrated
                </div>
              </div>
            </div>
          )}

          {/* Error / Success */}
          {error && (
            <div className={`text-sm rounded px-3 py-2 ${darkMode ? 'text-red-400 bg-red-900 bg-opacity-20' : 'text-red-700 bg-red-100'}`}>
              {error}
            </div>
          )}
          {success && (
            <div className={`text-sm rounded px-3 py-2 ${darkMode ? 'text-green-400 bg-green-900 bg-opacity-20' : 'text-green-700 bg-green-100'}`}>
              Handoff export started!
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex justify-end gap-3 px-6 py-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
          >
            Cancel
          </button>
          <button
            onClick={handleHandoff}
            disabled={!selectedTool || isExporting}
            className={`px-4 py-2 rounded-md flex items-center ${
              !selectedTool || isExporting
                ? 'bg-blue-800 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2" />
                Exporting...
              </>
            ) : (
              <>
                <FiDownload className="mr-2" />
                Handoff
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
